import React, {useMemo, useRef} from "react"
import {IntlProvider, IntlConfig, IntlFormatters} from "react-intl"

import {LangLoaderProvider, useLangStoreLang, useLangStoreMessages} from "./useLangLoader"
import {IntlCtx} from "./types"
import {createLangStore, LangLoaderInit} from "./langStore"

/**
 * This is not the context you are looking for...
 */
export const intlOverridesCtx = React.createContext<null | Pick<Props, "intlOverrides">>(null)

interface Props extends LangLoaderInit {
  children: React.ReactNode
  /** Only intended for native*/
  textComponent?: IntlConfig["textComponent"]
  /** override specific `Intl` methods - this is intended for web to inject the phrase editor */
  intlOverrides?: IntlOverrides
}

export type IntlOverrides = (intl: IntlFormatters) => Partial<IntlCtx>

/**
 * LangProvider is a wrapper around IntlProvider which fetches the latest keys from cms.
 * It supports an async storage mechanism to quickly reload cached keys on restart (intended for the native app).
 * Injected keys are also supported to allow native to bundle keys with the js bundle.
 *
 * If initialBuckets are provided, these will render immediately without waiting for the remote keys to be fetched.
 *
 * If keys are found in storage, these will override any keys initially set, and then be rendered.
 *
 * Finally when remote keys are loaded, these will override any initial or storage keys. The shared react context ensures
 * all components will receive the more up-to-date keys as they become available without blocking the app from rendering.
 */
export function LangProvider({
  children,
  textComponent,
  storage,
  initialMessages,
  contentFetcher,
  intlOverrides,
  favourRemote = []
}: Props) {
  const langStore = useRef(createLangStore({initialMessages, storage, contentFetcher, favourRemote})).current
  const overridesValue = useMemo(() => ({intlOverrides}), [intlOverrides])

  return (
    <LangLoaderProvider value={langStore}>
      <IntlWrapper textComponent={textComponent}>
        <intlOverridesCtx.Provider value={overridesValue}>{children}</intlOverridesCtx.Provider>
      </IntlWrapper>
    </LangLoaderProvider>
  )
}

type IntlWrapperProps = Pick<Props, "textComponent" | "children">
function IntlWrapper({textComponent, children}: IntlWrapperProps) {
  const messages = useLangStoreMessages()
  const locale = useLangStoreLang()

  return (
    <IntlProvider locale={locale} textComponent={textComponent} key={locale} messages={messages} onError={() => {}}>
      {children}
    </IntlProvider>
  )
}
